/* Free-sample chooser. Make handles Drive and Brevo using server-side connections. */
(function () {
  'use strict';

  const LEVELS = ['brevet', 'seconde', 'premiere', 'terminale'];
  const initialState = () => ({ level: null, product: null, step: 1 });

  function selectLevel(state, level) {
    if (!LEVELS.includes(level)) throw new Error('invalid_level');
    return { level, product: null, step: 2 };
  }

  function selectProduct(state, handle, catalog) {
    if (!catalog.some((item) => item.handle === handle && item.level === state.level)) {
      throw new Error('invalid_product');
    }
    return { ...state, product: handle, step: 3 };
  }

  function createPayload(state, contact, catalog) {
    const product = catalog.find((item) => item.handle === state.product && item.level === state.level);
    const firstName = (contact.firstName || '').trim();
    const email = (contact.email || '').trim();
    if (!product || !LEVELS.includes(state.level)) throw new Error('invalid_product');
    if (!firstName || firstName.length > 100) throw new Error('invalid_name');
    if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error('invalid_email');
    if (!['student', 'parent'].includes(contact.audience)) throw new Error('invalid_audience');
    if (contact.newsletterOptIn !== true) throw new Error('newsletter_required');
    return {
      schema: 'ines_free_sample_request.v1',
      source: 'shopify_extraits_gratuits',
      level: state.level,
      audience: contact.audience,
      product: { id: String(product.id), handle: product.handle },
      contact: { first_name: firstName, email },
      newsletter_opt_in: contact.newsletterOptIn === true,
    };
  }

  function createMakeAdapter(endpoint, fetchImpl = globalThis.fetch) {
    let url;
    try { url = new URL(endpoint); } catch (_) { return undefined; }
    if (url.protocol !== 'https:' || !/^hook\.(eu|us)\d+\.make\.com$/.test(url.hostname)
      || url.username || url.password || url.port || url.pathname === '/' || url.search || url.hash
      || typeof fetchImpl !== 'function') return undefined;
    return async (payload, { signal } = {}) => {
      const response = await fetchImpl(url.href, {
        method: 'POST', credentials: 'omit', redirect: 'error', cache: 'no-store',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal,
      });
      let result;
      try { result = await response.json(); } catch (_) { throw new Error('delivery_failed'); }
      if ((response.ok || response.status === 409) && result && result.ok === false && result.error === 'already_registered') throw new Error('already_registered');
      if (!response.ok) throw new Error('delivery_failed');
      // Make's generic HTTP 200 "Accepted" is not an email-send acknowledgement.
      if (!result || result.ok !== true) throw new Error('delivery_failed');
      return { ok: true };
    };
  }

  async function submitRequest(payload, adapter, timeoutMs = 30000) {
    if (typeof adapter !== 'function') throw new Error('unavailable');
    const controller = new AbortController();
    let timer;
    try {
      const response = await Promise.race([
        Promise.resolve().then(() => adapter(payload, { signal: controller.signal })),
        new Promise((_, reject) => { timer = setTimeout(() => { reject(new Error('timeout')); controller.abort(); }, timeoutMs); }),
      ]);
      if (!response || response.ok !== true) throw new Error('delivery_failed');
      return { ok: true };
    } finally {
      clearTimeout(timer);
    }
  }

  function getThankYouUrl(value, origin) {
    if (typeof value !== 'string' || !value.startsWith('/pages/')) return null;
    try {
      const url = new URL(value, origin);
      return url.origin === origin && !url.search && !url.hash ? url.href : null;
    } catch (_) { return null; }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { initialState, selectLevel, selectProduct, createPayload, submitRequest, createMakeAdapter, getThankYouUrl };
  }
  if (typeof document === 'undefined') return;

  function mount(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const form = root.querySelector('form');
    const makeAdapter = createMakeAdapter(root.dataset.makeWebhook);
    const catalog = Array.from(root.querySelectorAll('[data-product]')).map((button) => ({
      id: button.dataset.productId,
      handle: button.dataset.product,
      level: button.dataset.level,
      title: button.dataset.title,
      fullTitle: button.dataset.fullTitle,
      format: button.dataset.format,
    }));
    const levelButtons = Array.from(root.querySelectorAll('[data-choose-level]'));
    const productButtons = Array.from(root.querySelectorAll('[data-product]'));
    const firstName = form.elements.namedItem('first_name');
    const email = form.elements.namedItem('email');
    const newsletter = form.elements.namedItem('newsletter_opt_in');
    const audienceInputs = Array.from(form.querySelectorAll('[name="audience"]'));
    const status = root.querySelector('[data-status]');
    const submit = root.querySelector('[data-submit]');
    const submitLabel = submit.querySelector('span').textContent;
    const steps = Array.from(root.querySelectorAll('[data-step]'));
    let state = initialState();
    let busy = false;

    function clearStatus() {
      status.textContent = '';
      status.hidden = true;
      status.classList.remove('ines-samples__status--success');
    }

    function render(moveFocus = false) {
      const level = levelButtons.find((button) => button.dataset.chooseLevel === state.level);
      const product = catalog.find((item) => item.handle === state.product && item.level === state.level);
      for (const step of steps) {
        const number = Number(step.dataset.step);
        step.dataset.active = String(number === state.step);
        step.dataset.complete = String(number < state.step);
        step.querySelector('[data-panel]').hidden = number !== state.step;
        step.querySelector('[data-panel]').disabled = number !== state.step;
        const edit = step.querySelector('[data-edit]');
        if (edit) edit.hidden = number >= state.step;
      }
      root.querySelector('[data-level-summary]').textContent = level ? level.dataset.label : '';
      root.querySelector('[data-product-summary]').textContent = product ? `${product.title} · ${product.format}` : '';
      root.querySelector('[data-progress]').textContent = `${state.step} / 3`;
      for (const button of levelButtons) button.setAttribute('aria-pressed', String(button.dataset.chooseLevel === state.level));
      for (const button of productButtons) {
        button.hidden = button.dataset.level !== state.level;
        button.setAttribute('aria-pressed', String(button.dataset.product === state.product));
      }
      root.querySelector('[data-empty]').hidden = catalog.some((item) => item.level === state.level);
      if (product) {
        const selectedButton = productButtons.find((button) => button.dataset.product === product.handle && button.dataset.level === state.level);
        const image = selectedButton.querySelector('img');
        const summaryImage = root.querySelector('[data-selected-image]');
        summaryImage.hidden = !image;
        if (image) {
          summaryImage.src = image.currentSrc || image.src;
          summaryImage.alt = '';
        }
        root.querySelector('[data-selected-title]').textContent = product.title;
        root.querySelector('[data-selected-detail]').textContent = `${level.dataset.label} · ${product.format}`;
      }
      clearStatus();
      if (moveFocus) steps[state.step - 1].querySelector('[data-heading]').focus({ preventScroll: false });
    }

    root.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button || busy) return;
      if (button.hasAttribute('data-choose-level')) {
        state = selectLevel(state, button.dataset.chooseLevel);
        render(true);
      } else if (button.hasAttribute('data-product')) {
        state = selectProduct(state, button.dataset.product, catalog);
        render(true);
      } else if (button.hasAttribute('data-edit')) {
        state = { ...state, step: Number(button.dataset.edit) };
        render(true);
      }
    });

    function validateField(field) {
      const value = field.value.trim();
      const valid = field === firstName
        ? value.length > 0 && value.length <= 100
        : value.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && field.validity.valid;
      field.setAttribute('aria-invalid', String(!valid));
      root.querySelector(`[data-error="${field.name}"]`).hidden = valid;
      return valid;
    }

    [firstName, email].forEach((field) => {
      field.addEventListener('input', () => {
        if (field.getAttribute('aria-invalid') === 'true') validateField(field);
        clearStatus();
      });
    });

    function validateNewsletter() {
      const valid = newsletter.checked;
      newsletter.setAttribute('aria-invalid', String(!valid));
      root.querySelector('[data-error="newsletter_opt_in"]').hidden = valid;
      return valid;
    }
    newsletter.addEventListener('change', () => {
      validateNewsletter();
      clearStatus();
    });
    function validateAudience() {
      const valid = audienceInputs.some((input) => input.checked);
      audienceInputs.forEach((input) => input.setAttribute('aria-invalid', String(!valid)));
      root.querySelector('[data-error="audience"]').hidden = valid;
      return valid;
    }
    audienceInputs.forEach((input) => input.addEventListener('change', () => {
      validateAudience();
      clearStatus();
    }));

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (busy || state.step !== 3) return;
      if (form.elements.namedItem('hp_field')?.value) return;
      firstName.value = firstName.value.trim();
      email.value = email.value.trim();
      const nameValid = validateField(firstName);
      const emailValid = validateField(email);
      const audienceValid = validateAudience();
      const newsletterValid = validateNewsletter();
      if (!audienceValid || !nameValid || !emailValid || !newsletterValid) {
        (!audienceValid ? audienceInputs[0] : !nameValid ? firstName : !emailValid ? email : newsletter).focus();
        return;
      }
      const payload = createPayload(state, {
        firstName: firstName.value,
        email: email.value,
        newsletterOptIn: newsletter.checked,
        audience: audienceInputs.find((input) => input.checked).value,
      }, catalog);
      clearStatus();
      busy = true;
      form.setAttribute('aria-busy', 'true');
      const controls = Array.from(form.querySelectorAll('button, input'));
      const previousDisabled = controls.map((control) => control.disabled);
      controls.forEach((control) => { control.disabled = true; });
      submit.querySelector('span').textContent = root.dataset.sendingMessage || 'Envoi en cours…';
      try {
        await submitRequest(payload, window.InesFreeSamplesSubmit || makeAdapter);
        status.textContent = root.dataset.successMessage || 'Ta demande a bien été prise en compte. Consulte ta boîte mail pour retrouver tes fiches gratuites.';
        status.classList.add('ines-samples__status--success');
        const thankYouUrl = getThankYouUrl(root.dataset.thankYouUrl, window.location.origin);
        if (thankYouUrl) window.location.assign(thankYouUrl);
      } catch (error) {
        status.textContent = error.message === 'already_registered'
          ? root.dataset.existingContactMessage || 'Ces fiches sont réservées aux nouvelles inscriptions. Cette adresse est déjà enregistrée ou a déjà bénéficié d’un extrait gratuit.'
          : error.message === 'unavailable'
          ? root.dataset.unavailableMessage || "L’envoi des fiches gratuites n’est pas encore disponible. Aucune demande n’a été envoyée."
          : error.message === 'timeout'
            ? root.dataset.timeoutMessage || "L’envoi prend plus de temps que prévu. Vérifie ta boîte mail avant de réessayer."
            : root.dataset.failureMessage || "Ta demande n’a pas pu être confirmée. Vérifie ta boîte mail ou réessaie dans quelques instants.";
      } finally {
        busy = false;
        form.removeAttribute('aria-busy');
        controls.forEach((control, index) => { control.disabled = previousDisabled[index]; });
        submit.querySelector('span').textContent = submitLabel;
        status.hidden = false;
        status.focus();
      }
    });

    render();
    // Until initialization succeeds, the whole form is disabled (safe without JS).
    root.querySelector('[data-shell]').disabled = false;
    root.querySelector('[data-js-warning]').hidden = true;
  }

  function init(scope) {
    if (scope.matches && scope.matches('[data-ines-samples]')) mount(scope);
    scope.querySelectorAll('[data-ines-samples]').forEach(mount);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
